import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { errorMessage } from '../lib/api';
import { parseMoney } from '../lib/format';
import {
  createBusiness,
  listLeads,
  loseBusinesses,
  lossReasons as lossReasonsApi,
} from '../lib/resources';
import type { Business, Stage } from '../types';
import { Button, ErrorNote, Field, Modal, inputClass } from './ui';

/** Criar negócio a partir de uma etapa do Kanban. */
export function NewBusinessModal({
  stage,
  onClose,
  onCreated,
}: {
  stage: Stage;
  onClose: () => void;
  onCreated?: (business: Business) => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [leadId, setLeadId] = useState('');
  const [total, setTotal] = useState('');
  const [error, setError] = useState<string | null>(null);

  const leadsQuery = useQuery({
    queryKey: ['leads', 'picker', search],
    queryFn: () => listLeads({ search: search || undefined, take: 20 }),
  });

  const mutation = useMutation({
    mutationFn: () =>
      createBusiness({
        leadId,
        stageId: stage.id,
        total: total ? parseMoney(total) : undefined,
      }),
    onSuccess: (business) => {
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      onCreated?.(business);
      onClose();
    },
    onError: (mutationError) =>
      setError(errorMessage(mutationError, 'Não foi possível criar o negócio')),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!leadId) {
      setError('Escolha um lead');
      return;
    }
    setError(null);
    mutation.mutate();
  };

  return (
    <Modal title={`Novo negócio em ${stage.name}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Buscar lead">
          <input
            autoFocus
            className={inputClass}
            placeholder="Nome, e-mail ou telefone"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>

        <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200">
          {(leadsQuery.data?.data ?? []).map((lead) => (
            <button
              key={lead.id}
              type="button"
              onClick={() => setLeadId(lead.id)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                leadId === lead.id ? 'bg-slate-100 font-medium' : ''
              }`}
            >
              <span>{lead.name}</span>
              <span className="text-xs text-slate-400">{lead.phone ?? lead.email ?? ''}</span>
            </button>
          ))}
          {leadsQuery.data?.data.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-slate-400">
              Nenhum lead encontrado
            </p>
          )}
        </div>

        <Field label="Valor" hint="Opcional. Ao adicionar produtos, o total passa a vir deles.">
          <input
            className={inputClass}
            placeholder="R$ 0,00"
            value={total}
            onChange={(event) => setTotal(event.target.value)}
          />
        </Field>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Criando…' : 'Criar negócio'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Perder negócio: motivo obrigatório, justificativa quando o motivo exigir. */
export function LoseBusinessModal({
  businessId,
  onClose,
  onDone,
}: {
  businessId: string;
  onClose: () => void;
  onDone?: () => void;
}) {
  const queryClient = useQueryClient();
  const [reasonId, setReasonId] = useState('');
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reasonsQuery = useQuery({
    queryKey: ['loss-reasons'],
    queryFn: () => lossReasonsApi.list(),
  });
  const reasons = reasonsQuery.data?.data ?? [];
  const selected = reasons.find((reason) => reason.id === reasonId);

  const mutation = useMutation({
    mutationFn: () => loseBusinesses([businessId], reasonId, justification || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      queryClient.invalidateQueries({ queryKey: ['business', businessId] });
      onDone?.();
      onClose();
    },
    onError: (mutationError) =>
      setError(errorMessage(mutationError, 'Não foi possível marcar como perdido')),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!reasonId) {
      setError('Escolha o motivo da perda');
      return;
    }
    setError(null);
    mutation.mutate();
  };

  return (
    <Modal title="Marcar como perdido" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Motivo da perda *">
          <select
            className={inputClass}
            value={reasonId}
            onChange={(event) => setReasonId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {reasons.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.name}
              </option>
            ))}
          </select>
        </Field>

        {reasons.length === 0 && (
          <p className="text-xs text-slate-400">
            Nenhum motivo cadastrado. Crie em Configurações › Motivos de perda.
          </p>
        )}

        <Field
          label={selected?.requiredJustification ? 'Justificativa *' : 'Justificativa'}
          hint={selected?.requiredJustification ? 'Este motivo exige justificativa' : undefined}
        >
          <textarea
            rows={3}
            className={inputClass}
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
          />
        </Field>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="danger" disabled={mutation.isPending}>
            {mutation.isPending ? 'Salvando…' : 'Marcar como perdido'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
